import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import DeleteAccountModal from "@/components/DeleteAccountModal";
import { toast } from "sonner";
import { User, Mail, Briefcase, Lock, Bell, BadgeCheck, Save, AlertOctagon, Trash2 } from "lucide-react";

const ROLE_LABEL = { admin: "Administrator", supervisor: "Supervisor", employee: "Employee" };
export default function Settings() {
  const { user, refresh } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [savingTitle, setSavingTitle] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [jobTitle, setJobTitle] = useState("");

  useEffect(() => {
    api.get("/users/me/preferences")
      .then((r) => { setPrefs(r.data); setJobTitle(r.data.job_title || ""); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const toggleAlerts = async () => {
    if (!prefs) return;
    const next = !prefs.email_alerts;
    setPrefs({ ...prefs, email_alerts: next });
    try {
      const { data } = await api.patch("/users/me/preferences", { email_alerts: next });
      setPrefs(data);
      toast.success(next ? "Alerts enabled" : "Alerts disabled");
    } catch (e) {
      setPrefs({ ...prefs, email_alerts: !next });
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveTitle = async () => {
    setSavingTitle(true);
    try {
      const { data } = await api.patch("/users/me/preferences", { job_title: jobTitle });
      setPrefs(data);
      await refresh();
      toast.success("Job title updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingTitle(false); }
  };

  return (
    <div className="p-4 md:p-10 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Settings</div>
        <h1 className="font-heading text-4xl md:text-6xl">ACCOUNT</h1>
        <p className="text-muted-foreground mt-2 text-sm md:text-base">Manage your profile, security, and notifications.</p>
      </div>

      {/* Profile */}
      <section className="brutal-card p-5 md:p-7 mb-5">
        <h2 className="font-heading text-2xl flex items-center gap-2 mb-5">
          <User className="w-6 h-6 text-safety-yellow" strokeWidth={2.5} /> Profile
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <ReadOnlyField label="Name" Icon={BadgeCheck} value={user?.name || "—"} testId="profile-name" />
          <ReadOnlyField label="Role" Icon={BadgeCheck} value={ROLE_LABEL[user?.role] || "—"} testId="profile-role" />
          <ReadOnlyField label="Email" Icon={Mail} value={user?.email || "—"} testId="profile-email" />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5 mb-1.5">
              <Briefcase className="w-3.5 h-3.5" /> Job Title
            </label>
            <div className="flex gap-2">
              <input data-testid="profile-job-title" value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g. Foreman" className="industrial-input flex-1" />
              <button data-testid="save-job-title" onClick={saveTitle} disabled={savingTitle}
                className="min-h-[56px] px-4 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> {savingTitle ? "..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="brutal-card p-5 md:p-7 mb-5">
        <h2 className="font-heading text-2xl flex items-center gap-2 mb-5">
          <Lock className="w-6 h-6 text-safety-yellow" strokeWidth={2.5} /> Security
        </h2>
        <button data-testid="change-password-btn" onClick={() => setShowPw(true)}
          className="btn-outline w-full sm:w-auto">
          <Lock className="w-5 h-5 mr-2" strokeWidth={2.5} /> Change Password
        </button>
      </section>

      {/* Preferences */}
      <section className="brutal-card p-5 md:p-7">
        <h2 className="font-heading text-2xl flex items-center gap-2 mb-5">
          <Bell className="w-6 h-6 text-safety-yellow" strokeWidth={2.5} /> Notifications
        </h2>
        <div className="flex items-center justify-between gap-4 border-2 border-foreground/30 dark:border-safety-yellow/40 px-4 py-3">
          <div className="min-w-0">
            <div className="font-heading text-lg leading-tight">Email Expiration Alerts</div>
            <div className="text-xs text-muted-foreground mt-1">
              Get a reminder when your certifications enter the Expiring Soon (≤ 30 days) window.
            </div>
          </div>
          <Toggle data-testid="alerts-toggle"
            on={prefs?.email_alerts ?? true}
            onToggle={toggleAlerts} disabled={!prefs} />
        </div>
      </section>

      {/* Danger zone */}
      <section className="border-4 p-6 md:p-8" style={{ borderColor: "#FF3B30" }}>
        <div className="inline-flex items-center gap-2 mb-3">
          <AlertOctagon className="w-5 h-5" style={{ color: "#FF3B30" }} strokeWidth={3} />
          <h2 className="font-heading text-xl" style={{ color: "#FF3B30" }}>DANGER ZONE</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Permanently delete your KARM Safety account and every personal record tied to it.
          This action is irreversible.
        </p>
        <button
          type="button"
          data-testid="open-delete-account"
          onClick={() => setShowDelete(true)}
          className="w-full sm:w-auto min-h-[48px] px-5 inline-flex items-center justify-center gap-2 bg-status-expired text-white border-2 border-black font-black uppercase tracking-wider text-sm hover:opacity-90 transition-opacity"
        >
          <Trash2 className="w-4 h-4" strokeWidth={3} />
          Delete Account
        </button>
      </section>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
    </div>
  );
}

function ReadOnlyField({ label, Icon, value, testId, locked = false, hint = null }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div data-testid={testId}
        className="industrial-input bg-muted/40 dark:bg-safety-surface/60 flex items-center gap-2 font-bold truncate cursor-not-allowed">
        <span className="truncate flex-1">{value}</span>
        {locked && <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={2.5} aria-label="Locked" />}
      </div>
      {hint && (
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1.5">
          {hint}
        </div>
      )}
    </div>
  );
}

function Toggle({ on, onToggle, disabled, "data-testid": testId }) {
  return (
    <button type="button" role="switch" aria-checked={on} data-testid={testId}
      onClick={onToggle} disabled={disabled}
      className={`relative h-9 w-16 border-2 transition-colors flex-shrink-0 ${
        on ? "bg-safety-yellow border-black" : "bg-transparent border-foreground dark:border-safety-yellow"
      } disabled:opacity-50`}>
      <span className={`absolute top-0.5 h-7 w-7 border-2 border-black transition-all ${
        on ? "right-0.5 bg-black" : "left-0.5 bg-foreground"
      }`} />
    </button>
  );
}
